import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";

import { useGetAttachmentBuckets } from "api/queries/common/useGetAttachmentBuckets";
import { useGetQuoteRequestTimerSettings } from "api/queries/quotes/useGetQuoteRequestTimerSettings";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { useEditShipmentLock } from "@RequestFormV2/context/editShipmentLock";
import { useDraftProviderOptions } from "@RequestFormV2/hooks/useDraftProviderOptions";
import { areAllProvidersRequired } from "@RequestFormV2/sections/Provider/utils/defaultProviderInvitation";
import { DefaultProvidersInvitations } from "types/Permissions";

export function useProviderData() {
  const intl = useIntl();
  const isEditShipmentLock = useEditShipmentLock();

  const {
    providerOptions,
    minimumInvitesPossible,
    isLoading: isLoadingProviderOptions,
  } = useDraftProviderOptions({
    pruneStaleSelections: !isEditShipmentLock,
    enabled: true,
  });

  const location: string = useWatch({ name: "organizational.location" }) ?? "";
  const { parsedPermissions } = usePermissions();
  const defaultProvidersInvitation: string | undefined =
    parsedPermissions?.company_shipper_settings?.default_providers_invitation;
  const hasAttachmentBuckets: boolean = parsedPermissions?.company_shipper_settings?.request_attachment_buckets ?? false;
  const allProvidersRequired = areAllProvidersRequired(
    defaultProvidersInvitation,
    minimumInvitesPossible,
    providerOptions.length,
  );
  const isAllProvidersMandatory =
    defaultProvidersInvitation === DefaultProvidersInvitations.PROVIDERS_INVITATION_ALL_MANDATORY;

  const { data: bucketAttachments = [], isLoading: isLoadingBuckets } = useGetAttachmentBuckets(location, false, {
    enabled: !!location,
    refetchOnMount: false,
  });

  const {
    data: timingSettings,
    isLoading: isLoadingTimingSettings,
    isError: isTimingSettingsError,
  } = useGetQuoteRequestTimerSettings({
    refetchOnMount: false,
    onError: (err) =>
      errorToast(
        err,
        intl.formatMessage({
          id: "new-request.timing.couldNotRetrieveRequestTimer",
          defaultMessage: "Could not retrieve request timer settings. Please try again later.",
        }),
      ),
  });

  return useMemo(
    () => ({
      providerOptions,
      minimumInvitesPossible,
      defaultProvidersInvitation,
      allProvidersRequired,
      isAllProvidersMandatory,
      isLoadingProviderOptions,
      bucketAttachments,
      isLoadingBuckets,
      hasAttachmentBuckets,
      timingSettings,
      isLoadingTimingSettings,
      isTimingSettingsError,
    }),
    [
      providerOptions,
      minimumInvitesPossible,
      defaultProvidersInvitation,
      allProvidersRequired,
      isAllProvidersMandatory,
      isLoadingProviderOptions,
      bucketAttachments,
      isLoadingBuckets,
      hasAttachmentBuckets,
      timingSettings,
      isLoadingTimingSettings,
      isTimingSettingsError,
    ],
  );
}
